import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Send, Paperclip, FileText, Download, Pencil, Check, Save, Trash2, Minus } from "lucide-react";
import { api, uploadImage } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Loading, Avatar } from "@/components/primitives";
import EmojiGifPicker from "@/components/EmojiGifPicker";
import { applyShortcodes } from "@/lib/emoji";
import { fmtTime, isOnline } from "@/lib/format";
import { useMentions } from "@/lib/useMentions";
import MentionDropdown from "@/components/MentionDropdown";
import MentionText from "@/components/MentionText";

function Attachment({ m }) {
  if (m.attachment_type === "image") {
    return (
      <a href={m.attachment_url} target="_blank" rel="noreferrer" data-testid="livechat-attachment-image">
        <img src={m.attachment_url} alt={m.attachment_name || "image"} className="rounded-xl max-w-full max-h-56 object-cover border border-line" />
      </a>
    );
  }
  return (
    <a href={m.attachment_url} target="_blank" rel="noreferrer" download data-testid="livechat-attachment-file"
       className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-surface border border-line hover:bg-surface-3 transition-colors max-w-[240px]">
      <FileText className="w-5 h-5 text-brand shrink-0" />
      <span className="text-sm text-ink truncate flex-1">{m.attachment_name || "Attachment"}</span>
      <Download className="w-4 h-4 text-muted-foreground shrink-0" />
    </a>
  );
}

// Live, ephemeral 1:1 chat window. Rendered once at the app root so it persists across
// navigation; can be minimized to a floating pill while the rest of the app stays usable.
// Nothing is kept unless the member chooses Save on close (snapshots into their Notes).
export default function LiveChatWindow({ otherUserId, minimized, onMinimize, onRestore, onClose }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [closing, setClosing] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const pickerRef = useRef(null);
  const mentions = useMentions();

  const changeText = (raw) => { const t = applyShortcodes(raw); setText(t); mentions.onText(t); };

  const { data, isLoading } = useQuery({
    queryKey: ["live-chat", otherUserId],
    queryFn: () => api(`/live-chat/${otherUserId}`),
    refetchInterval: 3000,
  });

  const send = useMutation({
    mutationFn: (body) => api(`/live-chat/${otherUserId}/messages`, { method: "POST", body }),
    onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["live-chat", otherUserId] }); },
    onError: (e) => toast.error(e?.message || "Couldn't send message"),
  });

  const editMsg = useMutation({
    mutationFn: ({ id, text: t }) => api(`/live-chat/messages/${id}`, { method: "PUT", body: { text: t } }),
    onSuccess: () => { setEditingId(null); setEditText(""); qc.invalidateQueries({ queryKey: ["live-chat", otherUserId] }); },
    onError: (e) => toast.error(e?.message || "Couldn't edit message"),
  });

  const closeChat = useMutation({
    mutationFn: (action) => api(`/live-chat/${otherUserId}/close`, { method: "POST", body: { action } }),
    onSuccess: (_res, action) => {
      if (action === "save") { toast.success("Chat saved to your Notes"); qc.invalidateQueries({ queryKey: ["notes"] }); }
      qc.invalidateQueries({ queryKey: ["live-chat", otherUserId] });
      onClose();
    },
    onError: (e) => toast.error(e?.message || "Couldn't close chat"),
  });

  const saveEdit = (m) => { const t = editText.trim(); if (!t) return; editMsg.mutate({ id: m.id, text: t }); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      const isImg = (file.type || "").startsWith("image/");
      send.mutate({ text: "", attachment_url: url, attachment_name: file.name, attachment_type: isImg ? "image" : "file" });
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => { if (!minimized) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length, minimized]);

  const messages = data?.messages || [];
  const other = data?.other;
  const online = other?.online ?? isOnline(other?.last_active);

  // Shared Save / Delete prompt — rendered as a fixed, centered overlay so it works
  // whether the window is expanded or minimized.
  const prompt = closing ? (
    <div className="fixed inset-0 z-[80] bg-ink/50 flex items-center justify-center p-6" data-testid="livechat-close-prompt">
      <div className="bg-surface rounded-2xl border border-line shadow-xl p-5 w-full max-w-xs text-center">
        <h3 className="font-serif text-lg text-ink">Save this conversation?</h3>
        <p className="text-sm text-muted-foreground mt-1">Save it to your Notes, or delete it from your view.</p>
        <div className="flex flex-col gap-2 mt-4">
          <button type="button" data-testid="livechat-save-btn" disabled={closeChat.isPending}
                  onClick={() => closeChat.mutate("save")}
                  className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-brand text-white font-medium disabled:opacity-50">
            <Save className="w-4 h-4" />Save to Notes
          </button>
          <button type="button" data-testid="livechat-delete-btn" disabled={closeChat.isPending}
                  onClick={() => closeChat.mutate("delete")}
                  className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-red-300 text-red-600 font-medium hover:bg-red-50 disabled:opacity-50">
            <Trash2 className="w-4 h-4" />Delete
          </button>
          <button type="button" data-testid="livechat-cancel-close" onClick={() => setClosing(false)}
                  className="py-2 text-sm text-muted-foreground hover:text-ink">Keep chatting</button>
        </div>
      </div>
    </div>
  ) : null;

  // Minimized: a floating pill in the corner. The app stays fully usable behind it.
  if (minimized) {
    return (
      <>
        <div className="fixed bottom-4 right-4 z-[70]" data-testid="livechat-minimized">
          <div className="flex items-center gap-2 pl-2 pr-1 py-2 rounded-full bg-ink text-surface shadow-2xl border border-white/10">
            <button type="button" data-testid="livechat-restore" onClick={onRestore} className="flex items-center gap-2 pr-1">
              <span className="relative">
                <Avatar uri={other?.avatar_url} name={other?.full_name} size={34} />
                {online ? <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-ink" /> : null}
              </span>
              <span className="text-sm font-medium max-w-[120px] truncate">{other?.full_name || "Live chat"}</span>
            </button>
            <button type="button" data-testid="livechat-close" onClick={() => setClosing(true)}
                    className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-surface/70 hover:text-surface">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {prompt}
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:justify-end sm:p-6 bg-ink/40" data-testid="livechat-overlay">
      <div className="relative w-full sm:w-[420px] h-[85vh] sm:h-[600px] bg-surface rounded-t-2xl sm:rounded-2xl border border-line shadow-2xl flex flex-col overflow-hidden" data-testid="livechat-window">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line bg-surface-2">
          <Avatar uri={other?.avatar_url} name={other?.full_name} size={38} online={online} />
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-base text-ink truncate">{other?.full_name || "Live chat"}</h2>
            <p className={`text-[11px] ${online ? "text-emerald-600" : "text-muted-foreground"}`}>
              {online ? "● Online now" : "Offline"}
            </p>
          </div>
          <button type="button" data-testid="livechat-minimize" onClick={onMinimize}
                  className="w-8 h-8 rounded-full hover:bg-surface-3 flex items-center justify-center text-muted-foreground hover:text-ink">
            <Minus className="w-5 h-5" />
          </button>
          <button type="button" data-testid="livechat-close" onClick={() => setClosing(true)}
                  className="w-8 h-8 rounded-full hover:bg-surface-3 flex items-center justify-center text-muted-foreground hover:text-ink">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3 no-scrollbar">
          {isLoading ? <Loading /> : messages.map((m) => {
            const mine = m.from_user_id === user?.user_id;
            return (
              <div key={m.id} className={`group max-w-[85%] ${mine ? "self-end items-end" : "self-start items-start"} flex flex-col`}>
                <div className={`flex items-center gap-1.5 ${mine ? "flex-row-reverse" : "flex-row"}`}>
                  <div>
                    {m.attachment_url && <div className="mb-1"><Attachment m={m} /></div>}
                    {editingId === m.id ? (
                      <div data-testid={`livechat-edit-box-${m.id}`} className="flex flex-col gap-1.5 min-w-[220px]">
                        <textarea
                          data-testid={`livechat-edit-input-${m.id}`}
                          autoFocus spellCheck
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(m); } if (e.key === "Escape") { setEditingId(null); } }}
                          rows={2}
                          className="w-full text-sm bg-surface-2 border border-brand rounded-xl px-3 py-2 outline-none resize-none text-ink"
                        />
                        <div className="flex items-center gap-1.5 justify-end">
                          <button type="button" data-testid={`livechat-edit-cancel-${m.id}`} onClick={() => { setEditingId(null); setEditText(""); }}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-line text-muted-foreground hover:text-ink"><X className="w-3.5 h-3.5" />Cancel</button>
                          <button type="button" data-testid={`livechat-edit-save-${m.id}`} onClick={() => saveEdit(m)} disabled={!editText.trim() || editMsg.isPending}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-brand text-white disabled:opacity-50"><Check className="w-3.5 h-3.5" />Save</button>
                        </div>
                      </div>
                    ) : m.text ? (
                      <div className={`px-3.5 py-2.5 rounded-2xl ${mine ? "bg-ink text-surface rounded-br-md" : "bg-surface-2 border border-line text-ink rounded-bl-md"}`}>
                        <p className="text-sm font-sans whitespace-pre-wrap" data-testid={`livechat-msg-${m.id}`}><MentionText text={m.text} mentions={m.mention_users} /></p>
                      </div>
                    ) : null}
                  </div>
                  {editingId !== m.id && mine && m.text && (
                    <button type="button" data-testid={`livechat-edit-btn-${m.id}`}
                      onClick={() => { setEditingId(m.id); setEditText(m.text || ""); }}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-full text-muted-foreground hover:text-ink hover:bg-surface-3 flex items-center justify-center">
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className={`text-[10px] text-muted-foreground mt-0.5 ${mine ? "text-right" : ""}`} data-testid={`livechat-meta-${m.id}`}>
                  {fmtTime(m.created_at)}{m.edited_at ? <span data-testid={`livechat-edited-${m.id}`}> · edited</span> : null}
                </p>
              </div>
            );
          })}
          {!isLoading && messages.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">Say hello — this is a live chat</p>}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) { send.mutate({ text: text.trim(), mentions: mentions.mentionIds(text) }); mentions.reset(); pickerRef.current?.close(); } }}
              className="relative flex items-center gap-2 px-3 py-3 border-t border-line bg-surface-2">
          <MentionDropdown results={mentions.results} onPick={(mem) => setText(mentions.select(text, mem))} />
          <input ref={fileRef} type="file" className="hidden" data-testid="livechat-file-input"
                 accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" onChange={onFile} />
          <button type="button" data-testid="livechat-attach" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-10 h-10 rounded-full bg-surface border border-line text-ink flex items-center justify-center hover:bg-surface-3 disabled:opacity-50 shrink-0">
            <Paperclip className="w-5 h-5" />
          </button>
          <EmojiGifPicker
            ref={pickerRef}
            onEmoji={(em) => setText((t) => t + em)}
            onGif={(g) => send.mutate({ text: "", attachment_url: g.media_url, attachment_name: g.alt_text || "GIF", attachment_type: "image" })}
          />
          <input
            data-testid="livechat-input"
            spellCheck
            value={text}
            onChange={(e) => changeText(e.target.value)}
            placeholder={uploading ? "Uploading…" : "Message… @ to tag"}
            className="flex-1 bg-surface border border-line rounded-pill px-4 py-2.5 outline-none focus:border-brand text-sm"
          />
          <button data-testid="livechat-send" type="submit" disabled={!text.trim()} className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center disabled:opacity-50 shrink-0">
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
      {prompt}
    </div>
  );
}
